import React, { useCallback, forwardRef } from 'react';
import Joyride from 'react-joyride';
import { useTour } from './context/TourContext';
import { ChevronRight } from 'lucide-react';

const CustomTooltip = forwardRef(({ backProps, continuous, index, primaryProps, skipProps, step, tooltipProps = {}, size }, ref) => {
  const { ref: tooltipRef, ...restTooltipProps } = tooltipProps;
  const mergedRef = (el) => {
    if (ref) (typeof ref === 'function' ? ref(el) : (ref.current = el));
    if (tooltipRef) (typeof tooltipRef === 'function' ? tooltipRef(el) : (tooltipRef.current = el));
  };
  return (
  <div {...restTooltipProps} ref={mergedRef} className="w-[260px] max-w-[90vw] bg-slate-800 rounded-xl p-3 text-left shadow-xl border border-white/10 relative">
    {step.title && <h4 className="text-sm font-semibold text-white mb-1.5">{step.title}</h4>}
    <div className="text-xs text-white/80 leading-relaxed mb-3">{step.content}</div>
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-end gap-2">
        {index > 0 && (
          <button type="button" {...backProps} className="text-slate-400 hover:text-slate-300 text-xs font-medium transition-colors px-2 py-1">
            {backProps.title}
          </button>
        )}
        {continuous && (
          <button
            type="button"
            {...primaryProps}
            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
          >
            Paso {index + 1} de {size}
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <button type="button" {...skipProps} className="text-slate-400 hover:text-slate-300 text-xs font-medium transition-colors self-start">
        {skipProps.title}
      </button>
    </div>
  </div>
  );
});

CustomTooltip.displayName = 'CustomTooltip';

const TOUR_STEPS = [
  {
    target: '[data-tour="nav-inicio"]',
    content: 'Vista rápida de tus marcas, objetivos y optimizaciones de la IA. Todo resumido en la tabla de inicio.',
    disableBeacon: true,
  },
  {
    target: '[data-tour="brand-selector"]',
    content: 'Elegí la marca con la que trabajar y revisá el estado de tus conexiones (Google Ads, Meta Ads) para que la IA analice y genere optimizaciones.',
    disableBeacon: true,
  },
  {
    target: '[data-tour="nav-optimizaciones"]',
    content: 'Historial y detalle de las optimizaciones por cuenta. Se renuevan cada 7 días 💪',
    disableBeacon: true,
  },
  {
    target: '[data-tour="nav-objetivos"]',
    content: 'Creá, editá o eliminá objetivos a tu gusto. También podés generarlos con IA.',
    disableBeacon: true,
  },
  {
    target: '[data-tour="nav-campañas"]',
    content: 'Creá campañas en segundos con la IA. Editá anuncios, pujas y creatividades desde acá.',
    disableBeacon: true,
  },
  {
    target: '[data-tour="nav-analytics"]',
    content: 'Métricas de Google y Meta en un solo lugar: inversión, conversiones y clicks.',
    disableBeacon: true,
  },
];

const AppTour = () => {
  const { runTour, stopTour } = useTour();

  const handleJoyrideCallback = useCallback(
    (data) => {
      const { action, status } = data;
      if (action === 'close' || status === 'finished' || status === 'skipped') {
        stopTour();
      }
    },
    [stopTour]
  );

  return (
    <Joyride
      steps={TOUR_STEPS}
      run={runTour}
      continuous
      showSkipButton
      disableOverlayClose
      callback={handleJoyrideCallback}
      scrollToFirstStep
      spotlightClicks={false}
      tooltipComponent={CustomTooltip}
      floaterProps={{
        disableAnimation: false,
      }}
      styles={{
        options: {
          primaryColor: '#6366f1',
          zIndex: 100000,
          overlayColor: 'rgba(15, 23, 42, 0.85)',
          arrowColor: '#1e293b',
          backgroundColor: '#1e293b',
          textColor: '#f1f5f9',
        },
        tooltip: {
          borderRadius: 12,
          padding: 16,
          fontSize: 14,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: '#6366f1',
          color: '#fff',
          borderRadius: 8,
        },
        buttonBack: {
          color: '#94a3b8',
        },
        buttonSkip: {
          color: '#64748b',
        },
      }}
      locale={{
        back: 'Atrás',
        close: 'Cerrar',
        last: 'Finalizar',
        next: 'Siguiente',
        skip: 'Omitir',
      }}
    />
  );
};

export default AppTour;
